import Graph from './Graph';
import * as d3 from 'd3';
import { NCGroup, NCNode } from '../../../../src/commonTypes';
import { D3DragEvent } from 'd3';
import { calculateAlpha, ensurePhysicsAlphaIsHighEnough } from './graphPhysics';
import { isNodeDragTarget } from './nodeDragTarget';

export const MIN_MOVE_DISTANCE = 10;

export const initGroupDragBehavior = (graph: Graph) => {
	let movedGroup: boolean | undefined;
	let moveStartX: number | undefined;
	let moveStartY: number | undefined;
	let lastX: number | undefined;
	let lastY: number | undefined;

	const groupDragBehavior = d3
		.drag<SVGGElement, NCGroup>()
		.on('start', (event: D3DragEvent<SVGGElement, NCGroup, NCGroup>, g) => {
			
			movedGroup = false;
			moveStartX = event.x;
			moveStartY = event.y;
			lastX = event.x;
			lastY = event.y;
			graph.isDragging = true;



			graph.syncActiveNodeForDrag(undefined);
			// Unfix all nodes of the graph
			graph.nodes.forEach((n) => {
				delete n.fx;
				delete n.fy;
				n.moving = true;
			});
			graph.calculateDraggedNodeCluster(g.nodes);
			// Restart the simulation with a low alpha
			graph.simulation?.alpha(calculateAlpha(graph));
			graph.simulation?.alphaDecay(0);
			graph.simulation?.restart();
		})
		.on('drag', (event: D3DragEvent<SVGGElement, NCGroup, NCGroup>, g) => {


			if (lastX === undefined || lastY === undefined) {
				return;
			}

			movedGroup = true;

			// graph.simulation?.alpha(0.1);
			const diffX = event.x - lastX;
			const diffY = event.y - lastY;
			lastX = event.x;
			lastY = event.y;
			graph.currentDraggedNodeCluster?.forEach((n) => {
				n.x += diffX;
				n.y += diffY;
			});
			graph.onTick();
		})
		.on('end', (event: D3DragEvent<SVGGElement, NCGroup, NCGroup>, g) => {
			graph.isDragging = false;
			
			if (movedGroup && typeof moveStartX !== 'undefined' && typeof moveStartY !== 'undefined') {
				const moveDistance = Math.hypot(event.x - moveStartX, event.y - moveStartY);
				if (moveDistance > MIN_MOVE_DISTANCE) {
					if (neuroCreate.saveNodes) {
						neuroCreate.saveNodes.updateIfMoved(g.nodes, () => {
							graph.resetDraggedNodeCluster();
						});
					}
					graph.activateGroup(g);
				} else {
					// Did not move much, so reset position
					g.nodes.forEach((n) => {
						n.x = moveStartX!;
						n.y = moveStartY!;
					});
				}
			}
			graph.nodes.forEach((n) => {
				delete n.moving;
			});
		})
		.on('start.mouse', graph.onMouseLeave)
		.on('end.mouse', graph.onMouseMove);

	return groupDragBehavior;
};

export const initNodeDragBehavior = (graph: Graph) => {
	let isMovingConnectedNodes: boolean | undefined;
	let movedNode: boolean | undefined;
	let moveStartX: number;
	let moveStartY: number;
	let newX: number = 0;
	let newY: number = 0;

	const nodeDragBehavior = d3
		.drag<SVGGElement, NCNode>()
		.filter((event) => {
			// Filter out clicks on nodes when grouping is active
			const isSelectingNode = graph.isSelectingNode();
			if (isSelectingNode) {
				return false;
			}

			// Only allow drag on nodes and move-connected elements
			// Not on the text or menu items
			const mouseEvent = event as MouseEvent;
			const mouseTarget = mouseEvent.target as SVGElement;
			if (isNodeDragTarget(mouseTarget)) {
				return !mouseEvent.ctrlKey && !mouseEvent.button;
			} else {
				return false;
			}
		})
		.on('start', (event: D3DragEvent<SVGGElement, NCNode, NCNode>, n) => {
			const mouseEvent = event.sourceEvent as MouseEvent;
			const mouseTarget = mouseEvent.target as SVGElement;
			isMovingConnectedNodes = mouseTarget.closest('.move-connected') !== null;
			movedNode = false;
			newX = n.x;
			newY = n.y;
			moveStartX = n.x;
			moveStartY = n.y;
			// Unfix the node
			delete n.fx;
			delete n.fy;
			n.moving = true;
			graph.syncActiveNodeForDrag(n);
			
			graph.calculateDraggedNodeCluster([n]);
			// Restart the simulation with a low alpha
			graph.simulation?.alpha(calculateAlpha(graph));
			graph.simulation?.alphaDecay(0);
			graph.simulation?.restart();

			graph.isDragging = true;
			
			graph.nodes.forEach((n) => {
				delete n.fx;
				delete n.fy;
			});

			graph.getConnectedNodes(n).forEach((n1) => {
				delete n1.fx;
				delete n1.fy;
				n1.moving = true;
			});
		})
		.on('drag', (event: D3DragEvent<SVGGElement, NCNode, NCNode>) => {
			const diffX = event.x - event.subject.x;
			const diffY = event.y - event.subject.y;
			
			newX += diffX;
			newY += diffY;
			movedNode = true;
			
			ensurePhysicsAlphaIsHighEnough(graph);
			if (isMovingConnectedNodes) {
				graph.getConnectedNodes(event.subject).forEach((n) => {
					n.x += diffX;
					n.y += diffY;
				});
			} else {
				graph.currentDraggedNodeCluster?.forEach((n) => {
					n.x += diffX;
					n.y += diffY;
				});
			}
			graph.onTick();

			event.subject.x = event.x;
			event.subject.fx = event.x;
			event.subject.y = event.y;
			event.subject.fy = event.y;
		})
		.on('end', (e, n) => {
			delete n.moving;
			
			graph.isDragging = false;
			if (movedNode && typeof moveStartX !== 'undefined' && typeof moveStartY !== 'undefined') {
				const moveDistance = Math.hypot(n.x - moveStartX, n.y - moveStartY);
				if (moveDistance > MIN_MOVE_DISTANCE) {
					if (neuroCreate.saveNodes) {
						neuroCreate.saveNodes.updateIfMoved(graph.nodes, undefined, () => {
							graph.resetDraggedNodeCluster();
						});
					}
				} else {
					// Did not move much, so reset position
					n.x = moveStartX;
					n.y = moveStartY;
				}
			}
			graph.getConnectedNodes(n).forEach((n1) => {
				delete n1.moving;
			});
			n.fx = n.x;
			n.fy = n.y;

		})
		.on('start.mouse', graph.onMouseLeave)
		.on('end.mouse', graph.onMouseMove);

	return nodeDragBehavior;
};
